/**
 * Serverzustand der Web-Formulare.
 *
 * Die Einsendungen werden **nicht** lange vorgehalten: Sie sind die
 * Betriebsansicht eines Formulars ("kommt noch etwas an, und wenn nicht,
 * woran scheitert es?"), und eine veraltete Antwort darauf ist schlechter als
 * eine langsame.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../lib/apiError';
import {
  createWebForm,
  deleteWebForm,
  listSubmissions,
  listWebForms,
  updateWebForm,
  type WebFormCreate,
  type WebFormUpdate,
} from './api';

export const webFormKeys = {
  all: ['web-forms'] as const,
  submissions: (formId: string) => ['web-forms', formId, 'submissions'] as const,
};

const retryUnlessPermanent = (failureCount: number, error: unknown) =>
  !(error instanceof ApiError && error.isPermanent) && failureCount < 2;

export function useWebForms() {
  return useQuery({
    queryKey: webFormKeys.all,
    queryFn: listWebForms,
    staleTime: 5 * 60 * 1000,
    retry: retryUnlessPermanent,
  });
}

export function useSubmissions(formId: string | null) {
  return useQuery({
    queryKey: webFormKeys.submissions(formId ?? 'none'),
    queryFn: () => listSubmissions(formId ?? ''),
    enabled: formId !== null,
    retry: retryUnlessPermanent,
  });
}

function useInvalidate() {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: webFormKeys.all });
  };
}

export function useCreateWebForm() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (payload: WebFormCreate) => createWebForm(payload),
    onSuccess: invalidate,
  });
}

export function useUpdateWebForm() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ formId, payload }: { formId: string; payload: WebFormUpdate }) =>
      updateWebForm(formId, payload),
    onSuccess: invalidate,
  });
}

export function useDeleteWebForm() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (formId: string) => deleteWebForm(formId),
    onSuccess: invalidate,
  });
}
